import { ChangeEvent, useCallback, useEffect, useMemo, useState } from "react";
import Highlighter from "react-highlight-words";
import { Controller, FieldArrayWithId, useFieldArray, useFormContext } from "react-hook-form";
import { useParams } from "react-router-dom";
import { ArrowForwardIos, ErrorOutline, Replay, TextFormat } from "@mui/icons-material";
import {
  Box,
  Button,
  CircularProgress,
  outlinedInputClasses,
  Stack,
  styled,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography as T,
  Select,
  MenuItem,
  selectClasses,
  Tooltip,
  IconButton,
} from "@mui/material";
import MarkdownModal from "datasets/components/MarkdownModal";
import { StyledOutlinedInput } from "datasets/pages/tables/components/ImportDetails";
import IngestFormErrors from "datasets/pages/tables/components/IngestFormErrors";
import MarkdownCollapse from "datasets/pages/tables/components/MarkdownCollapse";
import useIngestContext from "datasets/pages/tables/contexts/ingest";
import useColumnDatatypes from "datasets/pages/tables/hooks/useColumnDatatypes";
import {
  GeneratedMetadata,
  Ingest,
  IngestColumn,
} from "datasets/pages/tables/types/interfaces/ingest";
import { RESERVED_KEYWORDS } from "datasets/pages/tables/utils/reserved-keywords";
import { checkTableExists, TABLE_EXISTS_WARNING } from "datasets/pages/tables/utils/tableExists";
import { StringParam, useQueryParam, withDefault } from "use-query-params";
import yn from "yn";
import { SearchIcon } from "~/assets/icons";
import useErrorToast from "~/contexts/error-toast";
import rumClient, { trackEvent } from "~/utils/monitoring";

const { VITE_CATALOG_API, VITE_DATASET_INGESTION_JOBS_ENABLED } = import.meta.env;

const StyledTHeaderCell = styled(TableCell)({
  fontWeight: 400,
  lineHeight: "20px",
  opacity: 0.6,
  padding: "8px 8px",
  "&:first-of-type": {
    paddingLeft: 16,
  },
  "&:last-of-type": {
    paddingRight: 16,
  },
});

const StyledTBodyCell = styled(TableCell)({
  padding: "20px 8px",
  position: "relative",
  "&:first-of-type": {
    paddingLeft: 16,
  },
  "&:last-of-type": {
    paddingRight: 16,
  },
});

const TableMetadataRow = ({
  autoExpand,
  column,
  disabled = false,
  highlightText,
  index,
  noBorder = false,
}: {
  autoExpand: boolean | null;
  column: FieldArrayWithId<Ingest, "metadata_config.columns", "id">;
  disabled: boolean;
  highlightText: string;
  index: number;
  noBorder?: boolean;
}) => {
  const { data: ingestTypes, isLoading: loadingIngestTypes } = useColumnDatatypes();
  const { control, register, setValue, trigger, watch } = useFormContext<Ingest>();
  const formColumn = watch(`metadata_config.columns.${index}`);

  const [columnDescriptionModalOpen, setColumnDescriptionModalOpen] = useState(false);

  const validDatatype = ingestTypes && ingestTypes.includes(column.type);

  const updateDescription = (description: string) => {
    setValue(`metadata_config.columns.${index}.description`, description);
    setColumnDescriptionModalOpen(false);
  };

  // Helps trigger validation for read-only 'name' field
  useEffect(() => {
    if (formColumn) {
      trigger(`metadata_config.columns.${index}`);
    }
  }, [formColumn, index, trigger]);

  return (
    <TableRow
      key={column.id}
      sx={{
        verticalAlign: "top",
        td: {
          overflowWrap: "break-word",
          wordBreak: "normal",
          ...(noBorder && { borderBottom: "none" }),
        },
        "&:last-of-type td": { borderBottom: "unset" },
      }}
    >
      <StyledTBodyCell>
        <Controller
          control={control}
          name={`metadata_config.columns.${index}.name`}
          rules={{
            required: true,
            validate: {
              correctStart: (value: string) =>
                /^[A-Za-z_]/.test(value) ? true : 'Must start with a letter or underscore "_".',
              validChars: (value: string) =>
                /^[A-Za-z0-9_$]*$/.test(value)
                  ? true
                  : 'Can only contain letters, numbers, "_" or "$".',
              notReserved: (value: string) =>
                !RESERVED_KEYWORDS.includes(value)
                  ? true
                  : `"${value}" is a reserved keyword and cannot be used.`,
            },
          }}
          render={({ field, fieldState: { error } }) => {
            const showUppercaseWarning = field.value !== field.value.toUpperCase();
            let tooltipMessage = "Column names are automatically converted to uppercase.";

            if (error && error.message) {
              tooltipMessage = error.message + " " + tooltipMessage;
            }

            return (
              <Stack gap={0.5}>
                <Box
                  sx={{
                    alignItems: "center",
                    color: error ? "attention.main" : undefined,
                    display: "flex",
                    gap: 0.5,
                    justifyContent: "space-between",
                    minHeight: 38,
                  }}
                >
                  <Highlighter
                    autoEscape
                    highlightClassName="highlight"
                    searchWords={[highlightText]}
                    textToHighlight={field.value.toUpperCase()}
                  />
                  {(showUppercaseWarning || error) && (
                    <Tooltip arrow title={tooltipMessage} enterDelay={500}>
                      {error ? (
                        <ErrorOutline sx={{ fontSize: 18 }} />
                      ) : (
                        <TextFormat sx={{ fontSize: 18, opacity: 0.8 }} />
                      )}
                    </Tooltip>
                  )}
                </Box>
              </Stack>
            );
          }}
        />
      </StyledTBodyCell>
      <StyledTBodyCell>
        <Select
          {...register(`metadata_config.columns.${index}.type`, {
            required: true,
          })}
          fullWidth
          disabled={disabled}
          size="small"
          defaultValue={validDatatype ? column.type : ingestTypes ? ingestTypes[0] : undefined}
          IconComponent={ArrowForwardIos}
          sx={{
            minHeight: 38,
            lineHeight: "20px",
            [`.${selectClasses.icon}`]: { transform: "rotate(90deg)", width: 16 },
            [`.${selectClasses.iconOpen}`]: { transform: "rotate(-90deg)" },
            [`.${selectClasses.outlined}`]: { p: 1 },
            [`.${outlinedInputClasses.notchedOutline}`]: { borderColor: "grey.300" },
          }}
        >
          {!loadingIngestTypes &&
            ingestTypes!.map((type) => (
              <MenuItem key={type} value={type}>
                {type}
              </MenuItem>
            ))}
        </Select>
      </StyledTBodyCell>
      <StyledTBodyCell sx={{ width: "100%" }}>
        <Controller
          control={control}
          name={`metadata_config.columns.${index}.description`}
          render={({ field }) => (
            <>
              <Box
                onClick={() => !disabled && setColumnDescriptionModalOpen(true)}
                sx={{
                  border: 1,
                  borderColor: "grey.300",
                  borderRadius: 1.5,
                  cursor: disabled ? "not-allowed" : "pointer",
                  display: "flex",
                  alignItems: "center",
                  lineHeight: "20px",
                  opacity: disabled ? 0.6 : 1,
                  p: 1,
                  transition: "border-color 150ms ease-in-out, background-color 150ms ease-in-out",
                  "&:hover": {
                    borderColor: disabled ? "grey.300" : "grey.400",
                    bgcolor: disabled ? undefined : "grey.50",
                  },
                }}
              >
                <MarkdownCollapse
                  autoExpand={autoExpand}
                  minHeight={0}
                  value={field.value}
                  maxLines={3}
                  placeholder="Click to add a description..."
                />
              </Box>
              {columnDescriptionModalOpen && (
                <MarkdownModal
                  onClose={() => setColumnDescriptionModalOpen(false)}
                  onSubmit={updateDescription}
                  open={columnDescriptionModalOpen}
                  title={`Edit description for ${column.name}`}
                  value={field.value ?? ""}
                />
              )}
            </>
          )}
        />
      </StyledTBodyCell>
    </TableRow>
  );
};

const TableMetadataDetails = ({ onNextPanel }: { onNextPanel: () => void }) => {
  const { datasetId } = useParams();
  const { showError } = useErrorToast();
  const { dataProfile, tableExists, setTableExists } = useIngestContext();
  const { control, getFieldState, setValue, watch, formState } = useFormContext<Ingest>();
  const sourceFile = watch("platform_storage_key");
  const sourceFileUri = watch("source_file_uri");
  const fileDelimiter = watch("metadata_config.file_format.delimiter");
  const columns = watch("metadata_config.columns");
  const tableName = watch("metadata_config.table");
  const databaseSchema = watch("metadata_config.database_schema");
  const tableDescription = watch("metadata_config.table_description");
  const tableFieldState = getFieldState("metadata_config.table");

  const { isLoading: loadingDatatypes } = useColumnDatatypes();

  const { fields } = useFieldArray({
    control,
    name: "metadata_config.columns",
  });

  const [columnSearch, setColumnSearch] = useQueryParam("search", withDefault(StringParam, ""));
  const [isGenerating, setIsGenerating] = useState(false);
  const [hasUsedCache, setHasUsedCache] = useState(false);
  const [descriptionModalOpen, setDescriptionModalOpen] = useState(false);
  const [debouncedTableName, setDebouncedTableName] = useState(tableName ?? "");

  // Debounce table name changes, optimistically clearing conflict state while re-checking.
  // Valid useEffect: manages a timer (external system).
  useEffect(() => {
    setTableExists(false);
    const id = setTimeout(() => setDebouncedTableName(tableName ?? ""), 300);
    return () => clearTimeout(id);
  }, [tableName, setTableExists]);

  // Check table existence after debounce. Valid useEffect: network request that cannot use
  // useFetcher because apiClient throws on 404, but here 404 means "doesn't exist" (not an error).
  useEffect(() => {
    if (!debouncedTableName || tableFieldState.invalid || !databaseSchema) {
      return;
    }

    let cancelled = false;

    checkTableExists(databaseSchema, debouncedTableName).then((exists) => {
      if (!cancelled) {
        setTableExists(exists);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [debouncedTableName, databaseSchema, tableFieldState.invalid, setTableExists]);

  // Reset generation state when source file changes (new file = fresh state)
  useEffect(() => {
    setHasUsedCache(false);
  }, [sourceFile, sourceFileUri]);

  const highlightText = columnSearch || "";

  // Use columns from watch instead of fields from useFieldArray for consistent state
  // useFieldArray's fields can be out of sync when parent form updates via setValue
  const columnsWithIds = useMemo(
    () =>
      (columns || []).map((col, idx) => ({
        ...col,
        id: fields[idx]?.id || `col-${idx}`,
      })),
    [columns, fields]
  );

  const filteredColumns = useMemo(
    () =>
      columnsWithIds.filter((column) => {
        return (
          column.name.toLowerCase().includes(columnSearch.toLowerCase()) ||
          column.description?.toLowerCase().includes(columnSearch.toLowerCase())
        );
      }),
    [columnSearch, columnsWithIds]
  );

  /**
   * Generate metadata using AI.
   * This is now opt-in only - user clicks the button when ready.
   *
   * Flow:
   * 1. Check if cached metadata already exists (GET /metadata)
   * 2. If not cached or stale, POST to /generate-metadata to start async generation
   * 3. Poll GET /metadata until status is COMPLETED or FAILED
   * 4. Apply results based on current column state
   *
   * Behavior:
   * - If columns are empty: Replace entirely with generated columns
   * - If columns exist: Merge generated data, validating column names match
   */
  const generateMetadata = useCallback(async () => {
    setIsGenerating(true);
    trackEvent("column_metadata_generated", {
      dataset_id: datasetId,
      table_name: tableName,
      database_schema: databaseSchema,
      column_count: columns?.length ?? 0,
      is_regeneration: hasUsedCache,
    });

    // No source file - cannot generate
    if (!sourceFile && !sourceFileUri) {
      setIsGenerating(false);
      return;
    }

    // Build query/body params: prefer source_file_uri (validated file) over platform_storage_key.
    // sourceFile is guaranteed to be defined here when sourceFileUri is falsy (guarded above).
    const fileKey = sourceFile as string;
    const metadataQueryParam = sourceFileUri
      ? `source_file_uri=${encodeURIComponent(sourceFileUri)}`
      : `platform_storage_key=${encodeURIComponent(fileKey)}`;
    const metadataPostBody = {
      ...(sourceFileUri ? { source_file_uri: sourceFileUri } : { platform_storage_key: fileKey }),
      table_description: tableDescription || "",
      ...(fileDelimiter && { file_delimiter: fileDelimiter }),
    };

    try {
      // Step 1: Check if we already have cached metadata
      const cacheCheckResp = await fetch(`${VITE_CATALOG_API}/metadata?${metadataQueryParam}`, {
        method: "GET",
      });

      let needsGeneration = true;
      let respData: GeneratedMetadata | null = null;

      if (cacheCheckResp.ok) {
        const cachedData: GeneratedMetadata = await cacheCheckResp.json();
        if (cachedData.status === "completed" && !hasUsedCache) {
          // First click this session: use cached result
          respData = cachedData;
          needsGeneration = false;
          setHasUsedCache(true);
        } else if (cachedData.status === "completed" && hasUsedCache) {
          // Subsequent clicks: force regeneration
          needsGeneration = true;
        } else if (cachedData.status === "pending" || cachedData.status === "in_progress") {
          // Generation already in progress, just poll for completion
          needsGeneration = false;
        }
        // If status is "failed" or 409 (stale), we'll regenerate
      }
      // 404 means no cached metadata, need to generate

      // Step 2: Start generation if needed (async - returns immediately with PENDING status)
      if (needsGeneration) {
        const postResp = await fetch(`${VITE_CATALOG_API}/generate-metadata`, {
          method: "POST",
          body: JSON.stringify(metadataPostBody),
          headers: { "Content-Type": "application/json" },
        });

        if (!postResp.ok) {
          const errorText = await postResp.text();
          throw new Error(
            `Failed to start metadata generation: HTTP ${postResp.status} - ${errorText}`
          );
        }
      }

      // Step 3: Poll GET /metadata until generation completes (skip if we already have completed data)
      if (!respData) {
        const pollInterval = 1000; // 1 second
        const maxAttempts = 120; // 2 minutes max
        let attempts = 0;

        while (attempts < maxAttempts) {
          attempts++;

          const getResp = await fetch(`${VITE_CATALOG_API}/metadata?${metadataQueryParam}`, {
            method: "GET",
          });

          if (!getResp.ok) {
            // 404 means cache entry doesn't exist yet, keep polling
            if (getResp.status === 404) {
              await new Promise((resolve) => setTimeout(resolve, pollInterval));
              continue;
            }
            const errorText = await getResp.text();
            throw new Error(`Failed to fetch metadata: HTTP ${getResp.status} - ${errorText}`);
          }

          const data: GeneratedMetadata = await getResp.json();

          if (data.status === "completed") {
            respData = data;
            break;
          } else if (data.status === "failed") {
            throw new Error("Metadata generation failed on the server");
          }

          // Still pending or in_progress, wait and poll again
          await new Promise((resolve) => setTimeout(resolve, pollInterval));
        }

        if (!respData) {
          throw new Error("Metadata generation timed out");
        }
      }

      // Step 4: Apply the generated metadata
      if (columns.length === 0) {
        // No existing columns - replace entirely with generated
        setValue("metadata_config.columns", respData.columns);
      } else {
        // Existing columns - validate and merge
        const existingNames = new Set(columns.map((c: IngestColumn) => c.name.toUpperCase()));
        const generatedNames = new Set(respData.columns.map((c) => c.name.toUpperCase()));

        // Check for mismatches
        const missingInGenerated = [...existingNames].filter((n) => !generatedNames.has(n));
        const extraInGenerated = [...generatedNames].filter((n) => !existingNames.has(n));

        if (missingInGenerated.length > 0 || extraInGenerated.length > 0) {
          // Column mismatch - show error and don't update
          const errors: string[] = [];
          if (missingInGenerated.length > 0) {
            errors.push(
              `Columns not found in generated metadata: ${missingInGenerated.join(", ")}`
            );
          }
          if (extraInGenerated.length > 0) {
            errors.push(`Extra columns in generated metadata: ${extraInGenerated.join(", ")}`);
          }
          showError(`Column mismatch: ${errors.join(". ")}. Generated metadata was not applied.`);
          return;
        }

        // Merge generated data into existing columns (preserve names from DataProfile)
        const mergedColumns = columns.map((col: IngestColumn) => {
          const generated = respData!.columns.find(
            (g) => g.name.toUpperCase() === col.name.toUpperCase()
          );
          return {
            ...col,
            type: col.type || generated?.type || "VARCHAR",
            description: generated?.description || col.description,
          };
        });

        setValue("metadata_config.columns", mergedColumns);
      }

      // Mark that we've used AI generation this session
      setHasUsedCache(true);
    } catch (e: any) {
      console.error(e);
      rumClient?.recordError(e);
      showError(e.message);
    } finally {
      setIsGenerating(false);
    }
  }, [
    columns,
    databaseSchema,
    datasetId,
    fileDelimiter,
    hasUsedCache,
    setValue,
    showError,
    sourceFile,
    sourceFileUri,
    tableDescription,
    tableName,
  ]);

  /**
   * Reset columns to the original DataProfile state.
   * Converts DataProfile columns to IngestColumn format.
   */
  const handleResetToProfile = useCallback(() => {
    if (!dataProfile?.columns) {
      return;
    }

    const profileColumns: IngestColumn[] = dataProfile.columns.map((col) => ({
      name: col.name,
      type: col.type,
      description: "",
      tags: [],
    }));

    setValue("metadata_config.columns", profileColumns);
  }, [dataProfile, setValue]);

  // Check if we have valid columns for ingestion
  const hasValidColumns = columns && columns.length > 0;
  // When feature flag is enabled, also check for valid table name
  const hasValidTableName = yn(VITE_DATASET_INGESTION_JOBS_ENABLED)
    ? tableName && !tableFieldState.invalid
    : true;
  const isFormValidForIngestion = formState.isValid && hasValidColumns && hasValidTableName;

  return (
    <>
      {yn(VITE_DATASET_INGESTION_JOBS_ENABLED) && (
        <Stack gap={0.5} width="100%">
          <Controller
            control={control}
            name="metadata_config.table"
            rules={{
              required: "Table name is required.",
              validate: {
                correctStart: (value: string) =>
                  /^[A-Za-z_]/.test(value) ? true : 'Must start with a letter or underscore "_".',
                validChars: (value: string) =>
                  /^[A-Za-z0-9_$]*$/.test(value)
                    ? true
                    : 'Can only contain letters, numbers, "_" or "$".',
                notReserved: (value: string) =>
                  !RESERVED_KEYWORDS.includes(value)
                    ? true
                    : `"${value}" is a reserved keyword and cannot be used.`,
              },
            }}
            render={({ field, fieldState: { error } }) => {
              return (
                <>
                  <T variant="body2" fontWeight={500}>
                    Name
                  </T>
                  <StyledOutlinedInput
                    {...field}
                    fullWidth
                    disabled={isGenerating}
                    error={!!error}
                    placeholder="Enter a display name for this table..."
                    size="small"
                    onChange={(e: ChangeEvent<HTMLInputElement>) => {
                      const formatted = e.target.value.toUpperCase().replace(/\s+/g, "_");
                      field.onChange(formatted);
                    }}
                  />
                  {error ? (
                    <T variant="caption" color="attention.main">
                      {error.message}
                    </T>
                  ) : tableExists ? (
                    <T variant="caption" color="attention.main">
                      {TABLE_EXISTS_WARNING}
                    </T>
                  ) : (
                    <T variant="caption" sx={{ opacity: 0.5 }}>
                      Table name must be in uppercase with no spaces.
                    </T>
                  )}
                </>
              );
            }}
          />
        </Stack>
      )}

      <Stack gap={0.5} width="100%">
        <T variant="body2" fontWeight={500}>
          Description
        </T>
        <Controller
          control={control}
          name="metadata_config.table_description"
          render={({ field }) => (
            <>
              <Box
                onClick={() => !isGenerating && setDescriptionModalOpen(true)}
                sx={{
                  border: 1,
                  borderColor: "grey.300",
                  borderRadius: 1.5,
                  cursor: isGenerating ? "not-allowed" : "pointer",
                  display: "flex",
                  alignItems: "center",
                  lineHeight: "20px",
                  minHeight: 38,
                  opacity: isGenerating ? 0.6 : 1,
                  p: 1,
                  transition: "border-color 150ms ease-in-out, background-color 150ms ease-in-out",
                  "&:hover": {
                    borderColor: "grey.400",
                    bgcolor: "grey.50",
                  },
                }}
              >
                <MarkdownCollapse
                  maxLines={4}
                  minHeight={0}
                  value={field.value ?? ""}
                  placeholder="Click to add a description..."
                />
              </Box>
              {descriptionModalOpen && (
                <MarkdownModal
                  onClose={() => setDescriptionModalOpen(false)}
                  onSubmit={(description) => {
                    field.onChange(description);
                    setDescriptionModalOpen(false);
                  }}
                  open={descriptionModalOpen}
                  title="Edit Table Description"
                  value={field.value ?? ""}
                />
              )}
            </>
          )}
        />
      </Stack>

      <Stack gap={1.25} width="100%">
        <T variant="body2" fontWeight={500}>
          Column Metadata
        </T>
        <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
          <Box display="flex" alignItems="center" gap={1}>
            <Button
              color="secondary"
              variant="contained"
              onClick={generateMetadata}
              disabled={
                isGenerating ||
                (!sourceFile && !sourceFileUri) ||
                (yn(VITE_DATASET_INGESTION_JOBS_ENABLED) && columns.length === 0)
              }
              endIcon={
                isGenerating ? <CircularProgress size={16} sx={{ color: "grey.500" }} /> : undefined
              }
              sx={{ bgcolor: "grey.100" }}
            >
              {hasUsedCache ? "Regenerate with AI" : "Generate with AI"}
            </Button>
            {/* Trash icon only shown when flag is ON (DataProfile available) */}
            {yn(VITE_DATASET_INGESTION_JOBS_ENABLED) && (
              <Tooltip arrow title="Reset to original columns" enterDelay={500}>
                <span>
                  <IconButton
                    disabled={!dataProfile || isGenerating}
                    onClick={handleResetToProfile}
                    sx={{ height: 39, width: 39 }}
                  >
                    <Replay />
                  </IconButton>
                </span>
              </Tooltip>
            )}
          </Box>
          <StyledOutlinedInput
            size="small"
            placeholder="Search by column name..."
            startAdornment={
              <Box display="flex" alignItems="center" justifyContent="center">
                <SearchIcon width={16} />
              </Box>
            }
            value={columnSearch}
            onChange={(event: ChangeEvent<HTMLInputElement>) => {
              const value = event.currentTarget.value;
              setColumnSearch(value, "replaceIn");
            }}
            sx={{ minWidth: 220 }}
          />
        </Box>
        <IngestFormErrors loading={isGenerating} />
        <TableContainer
          sx={{
            borderWidth: 1,
            borderStyle: "solid",
            borderColor: "grey.300",
            borderRadius: 1.5,
          }}
        >
          <Table size="small">
            <TableHead sx={{ bgcolor: "common.dimmedPageBackground" }}>
              <TableRow>
                <StyledTHeaderCell sx={{ minWidth: 300 }}>Name</StyledTHeaderCell>
                <StyledTHeaderCell sx={{ minWidth: 220 }}>Type</StyledTHeaderCell>
                <StyledTHeaderCell sx={{ minWidth: 360 }}>Description</StyledTHeaderCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredColumns.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={3} sx={{ textAlign: "center", py: 4, borderBottom: "none" }}>
                    <T variant="body2" color="text.secondary">
                      {columns.length === 0
                        ? 'No columns yet. Add a table name and description, then click "Generate with AI" to populate column metadata.'
                        : "No columns match your search."}
                    </T>
                  </TableCell>
                </TableRow>
              ) : filteredColumns.length === 1 ? (
                <TableMetadataRow
                  key={filteredColumns[0].id}
                  autoExpand={columnSearch === filteredColumns[0].name ? true : null}
                  column={filteredColumns[0]}
                  disabled={isGenerating || loadingDatatypes}
                  highlightText={highlightText}
                  index={0}
                  noBorder
                />
              ) : (
                filteredColumns.map((column, index) => (
                  <TableMetadataRow
                    key={column.id}
                    autoExpand={columnSearch === column.name ? true : null}
                    column={column}
                    disabled={isGenerating || loadingDatatypes}
                    highlightText={highlightText}
                    index={index}
                  />
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Stack>

      <Button
        variant="contained"
        disabled={isGenerating || !isFormValidForIngestion}
        onClick={() => onNextPanel()}
      >
        Next
      </Button>
    </>
  );
};

export default TableMetadataDetails;
